import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ShoppingCart, Heart, Zap } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

export const ProductCard = ({ product, index = 0 }) => {
  const { addToCart, buyNow } = useCart();
  const [imgLoaded, setImgLoaded] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Optimize image URL for faster loading (resize + WebP via our media proxy)
  const getOptimizedImageUrl = (url, w = 400) => {
    if (!url) return '';
    if (url.startsWith('/api/media/file/')) {
      return `${url}${url.includes('?') ? '&' : '?'}w=${w}`;
    }
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/\.(jpg|jpeg|png|webp)/, `_${w}x${w}.$1`);
    }
    return url;
  };

  // Calculate discount only if originalPrice exists and is greater than price
  const hasDiscount = product.originalPrice && product.originalPrice > product.price;
  const discount = hasDiscount 
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success(`${product.name} added to cart!`, {
      description: `Price: ${formatPrice(product.price)}`,
    });
  };

  const handleBuyNow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    buyNow(product, 1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative overflow-hidden rounded-xl bg-white border border-amber-100 hover:border-gold/40 transition-all duration-300 hover:shadow-lg">
          {/* Image Container */}
          <div className="relative aspect-square overflow-hidden bg-cream">
            {/* Tiny blurred placeholder — appears instantly, then the sharp image fades in */}
            <img
              src={getOptimizedImageUrl(product.image, 24)}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 w-full h-full object-cover blur-xl scale-110 transition-opacity duration-700 ${imgLoaded ? 'opacity-0' : 'opacity-100'}`}
            />
            <img
              src={getOptimizedImageUrl(product.image, 400)}
              alt={product.name}
              onLoad={() => setImgLoaded(true)}
              className={`relative w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
              loading="lazy"
              decoding="async"
            />
            
            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-col gap-2">
              {product.badge && (
                <span className="px-2.5 py-1 rounded-md gold-gradient text-white text-xs font-bold shadow-md">
                  {product.badge}
                </span>
              )}
            </div>

            {/* Discount Badge - Only show if there's a valid discount */}
            {hasDiscount && discount > 0 && (
              <div className="absolute top-3 right-3">
                <span className="px-2 py-1 rounded-md bg-green-500 text-white text-xs font-bold">
                  {discount}% OFF
                </span>
              </div>
            )}

            {/* Quick Actions - Always visible */}
            <div className="absolute bottom-3 left-3 right-3 flex gap-1.5">
              <Button
                onClick={handleAddToCart}
                className="flex-1 min-w-0 px-2 gold-gradient text-white hover:opacity-90 rounded-lg h-9 text-xs font-semibold shadow-lg"
                data-testid={`add-to-cart-${product.id}`}
              >
                <ShoppingCart className="w-3.5 h-3.5 mr-1 shrink-0" />
                Add
              </Button>
              <Button
                onClick={handleBuyNow}
                className="flex-1 min-w-0 px-2 bg-gradient-to-r from-orange-500 to-red-500 text-white hover:opacity-90 rounded-lg h-9 text-xs font-semibold shadow-lg"
                data-testid={`buy-now-${product.id}`}
              >
                <Zap className="w-3.5 h-3.5 mr-1 shrink-0" />
                <span className="sm:hidden">Buy</span>
                <span className="hidden sm:inline">Buy Now</span>
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Rating */}
            <div className="flex items-center gap-1.5 mb-2">
              <div className="flex items-center gap-0.5">
                <Star className="w-3.5 h-3.5 text-gold fill-gold" />
                <span className="text-sm font-semibold text-foreground">
                  {product.rating}
                </span>
              </div>
              <span className="text-xs text-muted-foreground">
                ({product.reviews.toLocaleString()})
              </span>
            </div>

            {/* Name */}
            <h3 className="font-medium text-foreground text-sm leading-tight mb-2 line-clamp-2 min-h-[2.5rem] group-hover:text-gold transition-colors">
              {product.name}
            </h3>

            {/* Price */}
            <div className="flex items-baseline gap-2">
              <span className="text-gold font-bold text-lg">
                {formatPrice(product.price)}
              </span>
              {hasDiscount && (
                <span className="text-muted-foreground line-through text-sm">
                  {formatPrice(product.originalPrice)}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
